import styles from './TrustedDomainTable.module.css';

import React, { useState } from 'react';

import useTranslation from 'components/hooks/useTranslation';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import { BngAddButton } from 'components/bng/ui/BngAddButton';
import { MODALS } from 'components/ui/redux/Actions';
import BngTable from 'components/bng/ui/BngTable';
import BngIconButton from 'components/bng/ui/BngIconButton';
import Dialog from 'components/ui/Dialog';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import UiMsg from 'components/ui/UiMsg';

const prohibitedDomains = [
  '@bimachine.com.br',
  '@gmail.com',
  '@outlook.com',
  '@hotmail.com',
  '@live.com',
  '@yahoo.com',
  '@ymail.com',
  '@yahoo.com.br',
  '@icloud.com',
  '@me.com',
  '@protonmail.com',
  '@aol.com',
  '@zoho.com',
  '@yandex.com',
  '@mail.com',
  '@gmx.com',
  '@uol.com.br',
  '@terra.com.br',
];

const formatDomain = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('@') ? trimmed.replace(/^@+/, '@') : `@${trimmed}`;
};

const hasTld = (raw) => {
  const plain = raw.replace(/^@+/, '');
  return /^([a-z0-9-]+\.)+[a-z0-9-]{2,}$/i.test(plain);
};

function AddTrustedDomainDialog({ closeModal = _.noop, value = '', onChange = _.noop }) {
  const { t } = useTranslation();
  const [newTrustedDomain, setNewTrustedDomain] = useState(value);
  const [error, setError] = useState('');

  return (
    <Dialog
      className={`AddTrustedDomainDialog ${styles.AddTrustedDomainDialog}`}
      title={t('add.trusted.domain')}
      newDialogLayout={true}
      onClose={closeModal}
    >
      <Dialog.Body>
        <label>{t('domain')}</label>
        <input
          className={`w-100 ${error ? styles.errorInput : ''}`}
          type="text"
          name="newTrustedDomain"
          value={newTrustedDomain}
          onChange={(e) => {
            setNewTrustedDomain(e.target.value);
            if (error) {
              setError('');
            }
          }}
          placeholder={t('trusted.domain.placeholder')}
        />
        {error && <p className={styles.errorMsg}>{error}</p>}
      </Dialog.Body>
      <Dialog.Footer>
        <DefaultDialogActions
          okLabel={t('save')}
          closeModal={closeModal}
          onClickSaveButton={() => {
            if (!hasTld(newTrustedDomain)) {
              setError(t('trusted.domain.missingTld'));
              return;
            }
            setError('');
            onChange({ value: formatDomain(newTrustedDomain), closeModal });
          }}
        />
      </Dialog.Footer>
    </Dialog>
  );
}

export default function TrustedDomainTable({ value = [], onChange = _.noop }) {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const editTrustedDomain = (idx = -1) => {
    return dispatch(
      MODALS.open(AddTrustedDomainDialog, {
        value: idx >= 0 ? value[idx] : undefined,
        onChange: ({ value: newDomain, closeModal }) => {
          const domainRegex = /^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

          if (!newDomain || !domainRegex.test(newDomain)) {
            UiMsg.error(t('invalid.domain.warning'));
            return;
          }

          if (value.includes(newDomain)) {
            UiMsg.error(t('duplicated.domain.warning'));
            return;
          }

          if (prohibitedDomains.includes(newDomain)) {
            UiMsg.error(t('prohibited.domain'));
            return;
          }

          const copy = value.slice();
          if (idx >= 0) {
            copy.splice(idx, 1, newDomain);
          } else {
            copy.push(newDomain);
          }

          onChange(copy);
          closeModal();
        },
      })
    );
  };

  return (
    <div className="TrustedDomainTable">
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <BngAddButton style={{ color: '#005dff', fontSize: '12px' }} onClick={() => editTrustedDomain()}>
          {t('add.trusted.domain')}
        </BngAddButton>
      </div>
      {value.length > 0 && (
        <div className={styles.TrustedDomainsTable}>
          <BngTable
            cols={[
              {
                key: 'domain',
                label: t('domain'),
                size: '85%',
                render: (row) => {
                  return (
                    <div>
                      <span>{row}</span>
                    </div>
                  );
                },
              },
              {
                key: 'actions',
                label: t('actions'),
                size: '15%',
                render: (row, index) => {
                  return (
                    <div className={styles.TrustedDomainsTableActions}>
                      <BngIconButton
                        icon={'edit'}
                        className="trusted-domain-edit"
                        title={t('edit')}
                        onClick={() => editTrustedDomain(index)}
                      />
                      <BngIconButton
                        icon="delete"
                        className="trusted-domain-remove"
                        title={t('remove')}
                        onClick={() => {
                          const copy = value.slice();
                          copy.splice(index, 1);
                          onChange(copy);
                        }}
                      />
                    </div>
                  );
                },
              },
            ]}
            rows={value}
            stickyHeader
          />
        </div>
      )}
    </div>
  );
}
